import { ActionButton, DefaultButton, Spinner, SpinnerSize, Stack, StackItem, TextField } from "@fluentui/react";
import React from "react";
import { AppPage } from "../helpers/enums";
import { LanguageContext } from "../helpers/LocalizationModule";
import { IAuthRequest } from "../interfaces/IAuthRequest";
import { IAuthResponse } from "../interfaces/IAuthResponse";
import { ITwoFactorCodeAuthRequest } from "../interfaces/ITwoFactorCodeAuthRequest";
import { useAppDispatch } from "../redux/hooks";
import { setCurrentPage } from "../redux/slices/navigationSlice";
import { setUser } from "../redux/slices/userSlice";
import { twoFactorCodeAuth, updateLastActivity } from "../services/userService";
import { StorageHelper } from "../helpers/StorageHelper";
import { ApiService } from "../services/apiService";
import { authenticate, refreshUserAccessToken } from "../services/authService";
import { termsText } from "../helpers/termsAndConditions";

export const LoginPage: React.FunctionComponent = () => {
    const languageStrings = React.useContext(LanguageContext);
    const dispatch = useAppDispatch();

    const [authReq, setAuthReq] = React.useState<IAuthRequest>({ email: "", password: "" });
    const [authReqCode, setAuthReqCode] = React.useState<ITwoFactorCodeAuthRequest>({ userId: undefined, twoFactorAuthCode: "" });
    const [isBusy, setIsBusy] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [userLocal, setUserLocal] = React.useState<IAuthResponse>();
    const [showTermsMessage, setShowTermsMessage] = React.useState(false);

    const onLogin = async (e: any) => {
        e.preventDefault();

        try {
            setIsBusy(true);
            const userResult = await authenticate(authReq);
            ApiService.setAuthToken(userResult.token);
            updateLastActivity(userResult.id);
            if (!userResult.twoFactorAuthEnabled) {
                setErrorMessage("");
                dispatch(setUser(userResult));
                StorageHelper.setItem("user", JSON.stringify(userResult));

                if (userResult.isFirstSignIn) {
                    setShowTermsMessage(true);
                } else {
                    dispatch(setCurrentPage(AppPage.HomePage));
                }
            } else {
                setErrorMessage("");
                setUserLocal(userResult);
                setAuthReqCode({ ...authReqCode, userId: userResult.id });
            }
        } catch (error: any) {
            console.error(error);
            if (error?.response?.data?.message) {
                if (error?.response?.data?.message === "Username or password is incorrect") {
                    setErrorMessage(languageStrings.ErrorUsernamePassword);
                } else {
                    setErrorMessage(error?.response?.data?.message);
                }
            } else {
                setErrorMessage(languageStrings.ErrorOccured);
            }
        } finally {
            setIsBusy(false);
        }
    };

    const onResetPassword = async (e: any) => {
        e.preventDefault();
        dispatch(setCurrentPage(AppPage.ResetPassword));
    };

    const onLoginCode = React.useCallback(
        async (e: any) => {
            e.preventDefault();

            try {
                setIsBusy(true);

                await twoFactorCodeAuth(authReqCode);

                dispatch(setUser(userLocal));
                StorageHelper.setItem("user", JSON.stringify(userLocal));
                if (userLocal?.isFirstSignIn) {
                    setShowTermsMessage(true);
                } else {
                    dispatch(setCurrentPage(AppPage.HomePage));
                }
            } catch (error: any) {
                setErrorMessage(error?.response?.data?.message ? error?.response?.data?.message : languageStrings.ErrorOccured);
            } finally {
                setIsBusy(false);
            }
        },
        [userLocal, authReqCode]
    );

    const onAcceptTerms = async () => {
        if (!userLocal) {
            const userStr = StorageHelper.getItem("user");
            if (userStr) {
                const user: IAuthResponse = JSON.parse(userStr);
                refreshUserAccessToken(user);
                updateLastActivity(user.id);
            }
        }

        setShowTermsMessage(false)
        dispatch(setCurrentPage(AppPage.HomePage));
    }

    return (
        <div className="theme-color md:bg-white h-[100%] w-full text-center flex">
            <div className="m-auto p-6 md:shadow-2xl theme-color">
                <div>
                    <img alt="BrumBrum" src="/logo.png" />
                </div>
                {errorMessage && <div className="bg-red-300 text-gray-800 mb-4">{errorMessage}</div>}
                {!userLocal && !showTermsMessage && (
                    <form onSubmit={onLogin}>
                        <div className="mb-2 w-full">
                            <TextField
                                value={authReq.email}
                                required
                                placeholder={languageStrings.Email}
                                type="email"
                                onChange={(e, newValue) => {
                                    setAuthReq({ ...authReq, email: newValue ? newValue : "" });
                                }}
                            />
                        </div>
                        <div className="mb-2">
                            <TextField
                                value={authReq.password}
                                required
                                placeholder={languageStrings.Password}
                                type="password"
                                canRevealPassword
                                onChange={(e, newValue) => {
                                    setAuthReq({ ...authReq, password: newValue ? newValue : "" });
                                }}
                            />
                        </div>
                        <div className="mx-16 mt-6">
                            <Stack>
                                <StackItem>
                                    <DefaultButton type="submit" disabled={isBusy} text={languageStrings.Continue} className="bg-black text-white hover:bg-black hover:text-white">
                                        {isBusy && <Spinner size={SpinnerSize.small} styles={{ root: { marginLeft: 5 } }}></Spinner>}
                                    </DefaultButton>
                                </StackItem>
                                <StackItem>
                                    <ActionButton className="h-5 mt-4 text-white hover:text-white" onClick={(e) => onResetPassword(e)}>{languageStrings.ForgottenPassword}</ActionButton>
                                </StackItem>
                            </Stack>
                        </div>
                    </form>
                )}
                {userLocal && !showTermsMessage && (
                    <form onSubmit={onLoginCode}>
                        <div className="mb-2">
                            <TextField
                                value={authReqCode.twoFactorAuthCode}
                                required
                                placeholder={languageStrings.SecurityCode}
                                onChange={(e, newValue) => {
                                    setAuthReqCode({ ...authReqCode, twoFactorAuthCode: newValue ? newValue : "" });
                                }}
                            />
                        </div>
                        <div className="mx-16 mb-2 mt-6">
                            <DefaultButton type="submit" disabled={isBusy} text={languageStrings.SignIn} className="bg-black text-white hover:bg-black hover:text-white">
                                {isBusy && <Spinner size={SpinnerSize.small} styles={{ root: { marginLeft: 5 } }}></Spinner>}
                            </DefaultButton>
                        </div>
                    </form>
                )}
                {showTermsMessage &&
                    <>
                        <div className="mb-2">
                            Voorwaarden & Privacyverklaring
                        </div>
                        <div className="mb-2">
                            <TextField multiline rows={15} borderless defaultValue={termsText} />
                        </div>
                        <div className="mb-2">
                            {languageStrings.AcceptTerms}
                        </div>
                        <div className="mx-16 mb-2">
                            <DefaultButton type="submit" text={languageStrings.Yes} className="bg-black text-white hover:bg-black hover:text-white" onClick={() => onAcceptTerms()}>
                                {isBusy && <Spinner size={SpinnerSize.small} styles={{ root: { marginLeft: 5 } }}></Spinner>}
                            </DefaultButton>
                        </div>
                    </>
                }
            </div>
        </div>
    );
};
